import { PreventPinchZoomService } from '@shared/services/prevent-pinch-zoom.service';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { APP_ROUTES } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import { provideState, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { OverlayContainer } from '@angular/cdk/overlay';
import { CdkOverlayContainer } from '@shared/services/cdk-overlay-container.service';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { trimLastSlashFromUrl } from '@3dsource/utils';
import { ErrorInterceptorService } from '@shared/services/error-interceptor.service';
import {
  type ApplicationConfig,
  ErrorHandler,
  inject,
  Injector,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import type { AppConfig } from '@shared/interfaces/app-config';
import { quicklinkProviders, QuicklinkStrategy } from 'ngx-quicklink';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { environment } from '../environments/environment';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { DEFAULT_TOASTR_CONFIG } from '@shared/constants/toastr.constant';
import { APP_CONFIG } from '@shared/tokens/global';
import { uploaderFeature } from '@shared/components/uploader/store/uploader.feature';
import { UploaderEffects } from '@shared/components/uploader/store/uploader.effects';
import { AuthGuard } from '@shared/guards/auth.guard';
import { createApollo } from '@shared/graphql/graphql';
import { GlobalErrorHandler } from '@shared/services/global-error-handler.service';
import type { UnrealInitialConfig } from '@3dsource/angular-unreal-module';
import { UNREAL_CONFIG } from '@3dsource/angular-unreal-module';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NoAuthGuard } from '@shared/guards/no-auth.guard';

export const createAppConfig = (config: AppConfig): ApplicationConfig => {
  const UNREAL_INITIAL_CONFIG: UnrealInitialConfig = {
    commandTelemetryReceiver: config.commandTelemetryReceiver,
    customErrorsEndpoint: config.customErrorsEndpoint,
    regionsPingUrl: config.regionsPingUrl,
    dataChannelConnectionTimeout: config.dataChannelConnectionTimeout,
  };
  return {
    providers: [
      provideZonelessChangeDetection(),
      provideToastr(DEFAULT_TOASTR_CONFIG),
      provideAnimationsAsync(),
      provideHttpClient(withInterceptorsFromDi()),
      provideRouter(
        APP_ROUTES,
        withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
        withComponentInputBinding(),
        withPreloading(QuicklinkStrategy),
      ),
      quicklinkProviders,
      provideStore({ router: routerReducer }),
      provideRouterStore(),
      provideBrowserGlobalErrorListeners(),
      provideState(uploaderFeature),
      provideEffects([UploaderEffects]),
      provideNamedApollo(() => {
        const httpLink = inject(HttpLink);
        const injector = inject(Injector);
        const create = (endpoint: string) =>
          createApollo(httpLink, injector, endpoint);
        return {
          legacy: create(config.api.graphql),
          default: create(config.api.graphql_v2),
          ecom: create(config.api.ecom),
        };
      }),
      provideAppInitializer(() => {
        inject(TraceService);
      }),
      { provide: APP_CONFIG, useValue: config },
      { provide: UNREAL_CONFIG, useValue: UNREAL_INITIAL_CONFIG },
      { provide: OverlayContainer, useClass: CdkOverlayContainer },
      {
        provide: APP_BASE_HREF,
        useFactory: (platformLocation: PlatformLocation) =>
          trimLastSlashFromUrl(platformLocation.getBaseHrefFromDOM()),
        deps: [PlatformLocation],
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptorService,
        multi: true,
      },
      { provide: ErrorHandler, useClass: GlobalErrorHandler },
      { provide: Sentry.TraceService, deps: [Router] },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: { appearance: 'outline' },
      },
      AuthGuard,
      NoAuthGuard,
      PreventPinchZoomService,
      environment.providers,
    ],
  };
};
